import { Link } from "react-router-dom";
import { useEffect } from "react";
import { Logo } from "@/components/Logo";

function LegalShell({ title, children }) {
  useEffect(() => { document.title = `${title} — Oven & Order`; window.scrollTo(0, 0); }, [title]);
  return (
    <div className="min-h-screen bg-cream paper-grain">
      <header className="border-b border-cocoa/10 bg-cream/80 backdrop-blur-xl">
        <div className="mx-auto max-w-3xl px-5 py-4"><Logo /></div>
      </header>
      <main className="mx-auto max-w-3xl px-5 py-12">
        <h1 className="font-serif text-4xl font-semibold text-cocoa">{title}</h1>
        <p className="mt-2 text-sm text-cocoa/50">Last updated {new Date().getFullYear()}</p>
        <div className="prose mt-8 space-y-5 text-[15px] leading-relaxed text-cocoa/80">{children}</div>
        <div className="mt-12 border-t border-cocoa/10 pt-6 text-sm">
          <Link to="/" className="font-medium text-terracotta hover:underline">← Back to Oven &amp; Order</Link>
        </div>
      </main>
    </div>
  );
}

const H = ({ children }) => <h2 className="font-serif text-2xl text-cocoa">{children}</h2>;

export function PrivacyPage() {
  return (
    <LegalShell title="Privacy Policy">
      <p>Oven &amp; Order ("we", "us") helps independent bakeries manage preorders. This policy explains what we collect and how we use it.</p>
      <H>Information we collect</H>
      <p>For bakery owners: your name, email, bakery details, and account credentials. For customers placing an order: your name, email, optional phone number, and order details. Payment card details are handled entirely by Stripe and are never stored by Oven &amp; Order.</p>
      <H>How we use information</H>
      <p>To operate your bakery account, process and confirm orders, send transactional emails (such as order confirmations), and — only where you have opted in — send bakery announcements. Each bakery's data is isolated from other bakeries.</p>
      <H>Marketing preferences</H>
      <p>Promotional emails are sent only to customers who have opted in, and every marketing email includes an unsubscribe link. Transactional order emails are separate and are not marketing.</p>
      <H>Payment processing</H>
      <p>Payments are processed by Stripe. We keep individual bakery customer payments separate from Oven &amp; Order subscription payments. We never collect or store raw card numbers.</p>
      <H>Data retention &amp; your rights</H>
      <p>We retain account and order data for as long as your account is active. To access, correct, or delete your data, contact us.</p>
      <H>Contact</H>
      <p>Questions? Email <a className="text-terracotta hover:underline" href="mailto:ovenandorder@gmail.com">ovenandorder@gmail.com</a>.</p>
    </LegalShell>
  );
}

export function TermsPage() {
  return (
    <LegalShell title="Terms of Service">
      <p>By creating an account or placing an order through Oven &amp; Order, you agree to these terms.</p>
      <H>Using Oven &amp; Order</H>
      <p>Bakery owners are responsible for the accuracy of their products, prices, ingredients, allergen and dietary claims, pickup details, and for fulfilling orders placed through their storefront. Oven &amp; Order provides the software; the bakery is the seller and merchant of record for its sales.</p>
      <H>Orders &amp; payments</H>
      <p>Customer payments made online are processed by Stripe and go directly to the bakery's connected account. Pay-at-pickup arrangements are between the customer and the bakery. Oven &amp; Order is not a party to those transactions.</p>
      <H>Subscriptions</H>
      <p>Bakery owner subscriptions are billed as described on our pricing page. You can manage or cancel your subscription from your account settings.</p>
      <H>Acceptable use</H>
      <p>Do not misuse the service, attempt to access other bakeries' data, or use it for unlawful purposes.</p>
      <H>Disclaimer</H>
      <p>The service is provided "as is." Bakery owners are solely responsible for compliance with applicable food, labeling, and cottage-food regulations in their area.</p>
      <H>Contact</H>
      <p>Questions? Email <a className="text-terracotta hover:underline" href="mailto:ovenandorder@gmail.com">ovenandorder@gmail.com</a>.</p>
    </LegalShell>
  );
}
